export interface MosChatBootOptions {
  getIdentityToken?: () => Promise<string>
  getPageUrl?: () => string
}

export interface MosChatApi {
  boot(options?: MosChatBootOptions): void
  show(): void
  hide(): void
  configure(layout?: { bottom?: number; right?: number; zIndex?: number }): void
  logout(): void
}

interface MosChatStub extends MosChatApi {
  _q: Array<[keyof MosChatApi, unknown?]>
}

declare global {
  interface Window {
    MPChat?: MosChatApi | MosChatStub
  }
}

export interface MosChatConfig {
  origin: string
  allowedHosts: string
}

const MOS_CHAT_SCRIPT_ID = "mp-mos-chat-widget"
// Ready and WorkKeys MUI modal layers start at 1300. Keep chat above ordinary app chrome while
// allowing shared fullscreen create/edit flows to cover it without route-specific logic.
const MOS_CHAT_Z_INDEX = 1299
let scriptPromise: Promise<void> | null = null

const parseAllowedHosts = (value: string): string[] =>
  value
    .split(",")
    .map(host => host.trim().toLowerCase())
    .filter(Boolean)

export const resolveMosChatOrigin = (
  config: MosChatConfig,
  hostname = window.location.hostname
): string | null => {
  if (!parseAllowedHosts(config.allowedHosts).includes(hostname.toLowerCase())) {
    return null
  }

  try {
    const url = new URL(config.origin)
    const localOrigin =
      url.hostname === "localhost" || url.hostname === "127.0.0.1"
    if (url.protocol !== "https:" && !(localOrigin && url.protocol === "http:")) {
      return null
    }
    if (
      url.username ||
      url.password ||
      url.pathname !== "/" ||
      url.search ||
      url.hash
    ) {
      return null
    }
    return url.origin
  } catch {
    return null
  }
}

const installMosChatStub = (): MosChatApi => {
  if (window.MPChat) return window.MPChat

  const queue: Array<[keyof MosChatApi, unknown?]> = []
  const stub: MosChatStub = {
    _q: queue,
    boot: options => queue.push(["boot", options]),
    show: () => queue.push(["show"]),
    hide: () => queue.push(["hide"]),
    configure: layout => queue.push(["configure", layout]),
    logout: () => queue.push(["logout"])
  }
  window.MPChat = stub
  return stub
}

export const loadMosChatScript = (origin: string): Promise<void> => {
  if (scriptPromise) return scriptPromise

  const existing = document.getElementById(
    MOS_CHAT_SCRIPT_ID
  ) as HTMLScriptElement | null
  if (existing?.dataset.loaded === "true") return Promise.resolve()

  scriptPromise = new Promise((resolve, reject) => {
    const script = existing ?? document.createElement("script")
    const loaded = () => {
      script.dataset.loaded = "true"
      resolve()
    }
    const failed = () => {
      script.remove()
      scriptPromise = null
      reject(new Error("MOS chat script failed to load"))
    }

    script.addEventListener("load", loaded, { once: true })
    script.addEventListener("error", failed, { once: true })
    if (!existing) {
      script.id = MOS_CHAT_SCRIPT_ID
      script.async = true
      script.src = `${origin}/widget.js`
      script.setAttribute("data-manual-boot", "")
      document.head.appendChild(script)
    }
  })

  return scriptPromise
}

export const showMosChat = async (
  origin: string,
  options: MosChatBootOptions
): Promise<void> => {
  const chat = installMosChatStub()
  chat.boot(options)
  chat.configure({ bottom: 45, zIndex: MOS_CHAT_Z_INDEX })
  chat.show()
  await loadMosChatScript(origin)
}

export const resetMosChat = (): void => {
  if (!window.MPChat) return
  // Widget cleanup must never prevent application sign-out. Keep the calls independent so a broken
  // optional global cannot stop the second half of the reset or escape into the auth flow.
  try {
    window.MPChat.logout()
  } catch {
    // Application sign-out remains authoritative even if the widget global is malformed.
  }
  try {
    window.MPChat.hide()
  } catch {
    // Cleanup is best-effort and must not escape into auth state transitions.
  }
}

export const currentPageWithoutSensitiveParams = (): string =>
  `${window.location.origin}${window.location.pathname}`

/** Test seam for the module-level single-load promise. Production code never calls this. */
export const resetMosChatLoaderForTests = (): void => {
  scriptPromise = null
  document.getElementById(MOS_CHAT_SCRIPT_ID)?.remove()
  delete window.MPChat
}
